import {
  Notification,
  ScheduledNotification,
} from "@pimo/notifications-plugin-v2";
import { BasicItem, Configuration, Profile, Project } from "vmd-types";
import { getDistributionConfig } from "vmd-utils";

import { STRAPI_URL } from "../env";
import { fetchWithCache, invalidateAppQueries } from "./query-cache";

export async function fetchSettings(language: string = "en"): Promise<{
  projects: Project[];
  basicItems: BasicItem[];
  configuration: Configuration;
  toolUsers?: Profile[];
  clientUsers?: Profile[];
}> {
  return fetchWithCache({
    queryKey: ["settings", language],
    staleTime: 5 * 60 * 1000,
    fallback: {
      projects: [],
      basicItems: [],
      toolUsers: [],
      clientUsers: [],
      configuration: getDistributionConfig(language as "en" | "de"),
    },
    queryFn: async () => {
      const response = await fetch(`${STRAPI_URL}/bff-settings`, {
        credentials: "include",
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (response.status >= 400) {
        throw new Error(`Fetch failed with status ${response.status}.`);
      }
      const json = (await response.json()) as {
        projects: Project[];
        basicItems: BasicItem[];
        configuration: Configuration;
        toolUsers?: Profile[];
        clientUsers?: Profile[];
      };

      return {
        ...json,
        configuration: json.configuration,
      };
    },
  });
}

export async function updateSettings(params: {
  projects: Project[];
  basicItems: BasicItem[];
  trackingItemsApplyToAllProjects: boolean;
  configuration: Configuration;
  toolUsers?: Profile[];
  clientUsers?: Profile[];
  hasUpdatedUsers?: boolean;
  notificationSettings: {
    notifications: Notification[];
    scheduledNotifications: ScheduledNotification[];
    published: boolean;
  };
}): Promise<
  | {
      basicItems: BasicItem[];
      configuration: Configuration;
      projects: Project[];
      toolUsers?: Profile[];
      clientUsers?: Profile[];
      notifications: Notification[];
      scheduledNotifications: ScheduledNotification[];
    }
  | { validationErrors: Record<string, unknown> }
  | undefined
> {
  const {
    basicItems,
    trackingItemsApplyToAllProjects,
    projects,
    configuration,
    toolUsers,
    clientUsers,
    hasUpdatedUsers,
    notificationSettings: { notifications, scheduledNotifications, published },
  } = params;

  try {
    const response = await fetch(`${STRAPI_URL}/bff-settings`, {
      credentials: "include",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        projects,
        basicItems,
        trackingItemsApplyToAllProjects,
        configuration: configuration,
        clientUsers: clientUsers,
        toolUsers: toolUsers,
        notificationSettings: {
          notifications,
          scheduledNotifications,
          published,
        },

        hasUpdatedUsers,
      }),
    });

    if (response.status >= 400) {
      throw new Error(`Fetch failed with status ${response.status}.`);
    }
    await invalidateAppQueries(["settings"]);
    await invalidateAppQueries(["dashboard"]);
    await invalidateAppQueries(["overview"]);
    await invalidateAppQueries(["overlay"]);
    await invalidateAppQueries(["entity-configuration"]);

    const json = (await response.json()) as {
      projects: Project[];
      basicItems: BasicItem[];
      configuration: Configuration;
      toolUsers?: Profile[];
      clientUsers?: Profile[];
      notifications: Notification[];
      scheduledNotifications: ScheduledNotification[];
    };
    return json;
  } catch {
    return;
  }
}

export async function clearSettings(params: { users?: Profile[] }): Promise<
  | {
      basicItems: BasicItem[];
      configuration: Configuration;
      projects: Project[];
      users?: Profile[];
    }
  | undefined
> {
  const { users } = params;

  try {
    const response = await fetch(`${STRAPI_URL}/bff-settings/clear`, {
      credentials: "include",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        users,
      }),
    });

    if (response.status >= 400) {
      throw new Error(`Fetch failed with status ${response.status}.`);
    }
    await invalidateAppQueries(["settings"]);
    await invalidateAppQueries(["dashboard"]);
    await invalidateAppQueries(["overview"]);
    await invalidateAppQueries(["overlay"]);
    await invalidateAppQueries(["entity-configuration"]);

    const json = (await response.json()) as {
      projects: Project[];
      basicItems: BasicItem[];
      configuration: Configuration;
      users?: Profile[];
    };
    return json;
  } catch {
    return;
  }
}
